import { CalendarDays, Camera, Gift, Music2, Snowflake, Sparkles, Utensils, WandSparkles } from "lucide-react";
import Seo from "@/components/Seo";
import "./NaughtyList.css";

const lookNaughtyClaus = "/assets/naughty-list/lookbook/01_naughty_claus.png";
const lookJingleBellRock = "/assets/naughty-list/lookbook/02_jingle_bell_rock.png";
const lookElfingAround = "/assets/naughty-list/lookbook/03_elfing_around.png";
const lookSantaBunny = "/assets/naughty-list/lookbook/04_santa_bunny.png";
const lookSnowBunny = "/assets/naughty-list/lookbook/05_snow_bunny.png";
const lookSilentNight = "/assets/naughty-list/lookbook/06_silent_night.png";
const santa = "https://d3u0tzju9qaucj.cloudfront.net/5545da0a-09a0-403c-9f6b-56548de82dc9/3f99ea9f-d52c-40ec-815e-80707d1936a0.png";
const snow = "https://d3u0tzju9qaucj.cloudfront.net/5545da0a-09a0-403c-9f6b-56548de82dc9/56ad9874-b4f7-43ef-97ba-61c03a8b5963.png";
const heels = "https://d3u0tzju9qaucj.cloudfront.net/5545da0a-09a0-403c-9f6b-56548de82dc9/f9e2dc7c-1a07-4ad6-8d4b-60ab6b3a29ed.png";
const gifts = "https://d3u0tzju9qaucj.cloudfront.net/5545da0a-09a0-403c-9f6b-56548de82dc9/9765e364-a796-4538-8bf6-662bb9f0f6e7.png";
const evidence = "https://d3u0tzju9qaucj.cloudfront.net/5545da0a-09a0-403c-9f6b-56548de82dc9/365ed68b-fb61-4418-87c4-c1aaee0d631b.png";

const inclusions = [
  [Sparkles, "Guided pole session", "Warm-up + beginner-friendly combinations designed to look good on camera."],
  [Camera, "Editorial photo set", "A styled Christmas-after-dark set with guided posing and flash photography."],
  [Gift, "2 physical Polaroids", "Leave with tangible Naughty List evidence from the night."],
  [Music2, "North Pole After Dark", "R&B, hip-hop, club energy and holiday edits throughout the experience."],
  [Utensils, "Small bites", "Holiday bites and mocktails during the social break."],
  [Snowflake, "Beginner friendly", "Heels are optional. Pole-safe styling and confidence are the only requirements."],
];

const moments = [
  ["01", "Check In", "Naughty List registration, waivers and your official event identity."],
  ["02", "Santa’s Stretch", "A guided warm-up before heels, poses and pole work."],
  ["03", "Elf Training", "Beginner-friendly combinations built to photograph well."],
  ["04", "Milk & Cookies", "Small bites, drinks, music and a reset before the cameras come out."],
  ["05", "Auditions", "Freestyle, character energy and your moment to sleigh."],
  ["06", "Evidence", "Editorial portraits + physical Polaroids to take home."],
];

const archetypes = [
  ["NAUGHTY CLAUS", lookNaughtyClaus, "Red velvet, white faux fur, boots, gloves and main-character energy.", "RED VELVET"],
  ["JINGLE BELL ROCK", lookJingleBellRock, "Red minis, stockings, sparkle and holiday pop-girl attitude.", "HOLIDAY GLAM"],
  ["ELFING AROUND", lookElfingAround, "Green and red, striped details, suspenders and playful workshop styling.", "WORKSHOP"],
  ["SANTA BUNNY", lookSantaBunny, "Bunny-inspired holiday styling, cuffs, bows, thigh highs and editorial polish.", "PLAYFUL"],
  ["SNOW BUNNY", lookSnowBunny, "White, silver, icy blue, faux fur and crystal shine.", "ICE COLD"],
  ["SILENT NIGHT", lookSilentNight, "Black velvet, diamonds, sheer details and dramatic Christmas-after-dark styling.", "AFTER DARK"],
];

const faqs = [
  ["Do I need pole experience?", "No. The event is designed to be beginner friendly, with guided warm-up and photo-ready combinations."],
  ["Do I have to wear heels?", "No. Heels are optional. Wear something you can move safely in and that fits your chosen character."],
  ["What should I wear?", "Choose one of the six North Pole character directions or create your own holiday fantasy within the pole-safe dress code."],
  ["Are photos included?", "Yes. The experience includes editorial photography plus two physical Polaroid-style keepsakes."],
];

function SectionTitle({ kicker, title, copy, align = "left" }) {
  return (
    <div className={"nl-section-title " + (align === "center" ? "is-center" : "")}>
      <div className="nl-kicker">{kicker}</div>
      <h2>{title}</h2>
      {copy && <p>{copy}</p>}
    </div>
  );
}

export default function NaughtyList() {
  return (
    <main className="nl-page">
      <Seo title="P&P: The Naughty List | Pole & Polaroids" description="Pole & Polaroids: The Naughty List — a Christmas-after-dark pole and photography experience on December 18, 2026." path="/pole-and-polaroids/naughty-list" robots="noindex,nofollow" />
      <div className="nl-bg" aria-hidden="true" />

      <nav className="nl-nav nl-logo-strip">
        <img
          className="nl-logo-image"
          src="/assets/naughty-list/pole-polaroids-christmas-header.jpeg"
          alt="Pole & Polaroids Christmas header — 12.18.26"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </nav>

      <div className="nl-light-divider" aria-hidden="true">
        <img className="nl-light-divider-img" src="/assets/naughty-list/christmas-lights-transparent.png" alt="" loading="eager" decoding="async" fetchPriority="high" />
        <div className="nl-light-sparkles">
          {Array.from({ length: 10 }).map((_, i) => <span key={i} className={"sp" + (i + 1)} />)}
        </div>
      </div>

      <section className="nl-hero">
<div className="nl-hero-copy">
          <div className="nl-kicker">POLE & POLAROIDS PRESENTS</div>
          <h1><span>THE</span><span className="nl-title-outline">NAUGHTY</span><span>LIST</span></h1>
          <p className="nl-motto">Pole’n Around The X-MAS Tree</p>
          <p className="nl-intro">Pole class. Holiday fantasy. Editorial photos. Polaroids. North Pole after closing.</p>
          <div className="nl-hero-actions">
            <a href="#tickets" className="nl-button nl-button-primary">COME SLEIGH</a>
            <a href="#dresscode" className="nl-button nl-button-ghost">PLAN THE FIT</a>
          </div>
          <div className="nl-event-strip">
            <div><CalendarDays size={17} /> DECEMBER 18, 2026</div>
            <div><Camera size={17} /> POLE + POLAROIDS</div>
          </div>
        </div>
      </section>

      <section className="nl-includes nl-shell">
        <SectionTitle kicker="YOUR NIGHT INCLUDES" title="More than a pole class." copy="A complete holiday experience built around movement, style, photography and something physical to take home." />
        <div className="nl-includes-grid">
          {inclusions.map(([Icon, title, copy]) => (
            <article className="nl-include-card" key={title}>
              <Icon size={22} />
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="nl-visual-reel" aria-label="The Naughty List campaign">
        <article className="nl-scene">
          <img src={santa} alt="Santa Bunny holiday editorial" loading="lazy" decoding="async" />
          <div className="nl-scene-copy"><span>THE LOOK</span><h2>Santa Bunny</h2><p>Playful holiday editorial energy.</p></div>
        </article>
        <article className="nl-scene">
          <img src={snow} alt="Snow Bunny holiday editorial" loading="lazy" decoding="async" />
          <div className="nl-scene-copy"><span>THE FANTASY</span><h2>Snow Bunny</h2><p>Silver, white, icy light and faux fur.</p></div>
        </article>
      </section>

      <section id="experience" className="nl-section nl-shell">
        <SectionTitle kicker="THE EXPERIENCE" title="From check-in to evidence." copy="The evening has a simple flow, so you always know what comes next." />
        <div className="nl-moment-grid">
          {moments.map(([num, title, copy]) => (
            <article className="nl-moment" key={num}>
              <div className="nl-number">{num}</div>
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="dresscode" className="nl-lookbook">
        <div className="nl-shell">
          <SectionTitle kicker="THE LOOKBOOK" title="Choose your North Pole character." copy="Pick a lane or remix one. The goal is holiday fantasy that still lets you move comfortably and safely." />
          <div className="nl-look-grid">
            {archetypes.map(([title, image, copy, tag]) => (
              <article className="nl-look" key={title}>
                <div className="nl-look-image"><img src={image} alt={title + " outfit inspiration"} loading="lazy" decoding="async" /></div>
                <div className="nl-look-copy">
                  <span>{tag}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="nl-backroom nl-shell">
        <div className="nl-backroom-photo">
          <img src={gifts} alt="Holiday gifts, fur and ornaments editorial set" loading="lazy" decoding="async" />
        </div>
        <div className="nl-backroom-copy">
          <div className="nl-kicker">THE SET</div>
          <h2>Santa’s Back Room.</h2>
          <p>Step into red velvet, faux fur, chrome, presents and flash photography for your official Naughty List evidence.</p>
          <div className="nl-mini-meta"><span>DIRECT FLASH</span><span>RED VELVET</span><span>CHROME</span><span>FUR</span></div>
        </div>
      </section>

      <section className="nl-evidence-wall nl-shell">
        <div className="nl-evidence-header">
          <div>
            <div className="nl-kicker">NORTH POLE EVIDENCE</div>
            <h2>Proof you were there.</h2>
          </div>
          <strong>CASE FILE · 12.18.26</strong>
        </div>
        <div className="nl-evidence-grid">
          <article className="nl-evidence-card">
            <img src={santa} alt="Santa Bunny evidence portrait" loading="lazy" decoding="async" />
            <div><span>CASE 001</span><b>CAUGHT</b><small>Santa Bunny · after hours</small></div>
          </article>
          <article className="nl-evidence-card">
            <img src={heels} alt="Heels and chrome pole detail" loading="lazy" decoding="async" />
            <div><span>CASE 002</span><b>RED VELVET</b><small>Chrome + heels + fur</small></div>
          </article>
          <article className="nl-evidence-card">
            <img src={snow} alt="Snow Bunny evidence portrait" loading="lazy" decoding="async" />
            <div><span>CASE 003</span><b>ICE COLD</b><small>Still on the list</small></div>
          </article>
          <div className="nl-evidence-note"><span>OFFENSE</span><strong>POLE’N AROUND<br />ON COMPANY TIME</strong><small>North Pole Security Archive</small></div>
        </div>
      </section>

      <section className="nl-feature-band nl-shell">
        <article><Music2 size={26} /><div><span>MUSIC</span><h3>North Pole After Dark</h3><p>R&B, hip-hop, club energy and holiday edits.</p></div></article>
        <article><Utensils size={26} /><div><span>FOOD</span><h3>The Naughty List Menu</h3><p>Small bites, sweets and mocktails between sessions.</p></div></article>
        <article><Gift size={26} /><div><span>TAKE IT HOME</span><h3>Physical Evidence</h3><p>Two Polaroid-style keepsakes from the night.</p></div></article>
      </section>

      <section className="nl-faq nl-shell">
        <SectionTitle kicker="BEFORE YOU COME" title="Quick answers." />
        <div className="nl-faq-grid">
          {faqs.map(([q, a]) => <article key={q}><h3>{q}</h3><p>{a}</p></article>)}
        </div>
      </section>

      <section id="tickets" className="nl-ticket-section">
        <WandSparkles size={30} />
        <div className="nl-kicker">TEST EXPERIENCE · TICKETS NOT LIVE</div>
        <h2>Christmas gets naughty<br />December 18.</h2>
        <p>Ticket pricing, capacity and checkout will appear here once the final event details are approved.</p>
        <button className="nl-button nl-button-disabled" type="button" disabled>TICKETS COMING SOON</button>
      </section>

      <footer className="nl-footer nl-shell">
        <div><strong>POLE & POLAROIDS</strong><span>by Fotos on the Geaux</span></div>
        <div>THE NAUGHTY LIST · 12.18.26 · COME SLEIGH.</div>
      </footer>
    </main>
  );
}
